import React, { createContext, useContext, useState } from "react";
import { ERROR, LOADING, SUCCESS } from "../constants/common";
import { useAlert } from "./Alert";
import {
  getFacilityInfo,
  updateFacilityBasicInfo,
  updateFacilityContact,
  updateFacilityServices,
  updateFacilityPhoto,
  updateFacilityFaqs,
  uploadFacilityImages,
  removeFacilityImage,
  removeFacilityPhoto,
  updateAboutMe,
  updateSessionFormats,
} from "../services/organization.services";
import { geocodeAddress } from "../services/common.services";
import { ReactComponent as WarningIcon } from "../assets/Icons/warning.svg";
import { useCommon } from "./common";

const GeneralProfileContext = createContext({});

export const GeneralProfileContextProvider = ({ children }) => {
  const { sendAlert } = useAlert();
  const [generalProfileState, setGeneralProfileState] = useState({
    updateFacilityBasicInfoStatus: "",
    updateFacilityContactStatus: "",
    updateFacilityServicesStatus: "",
    updatePhotoStatus: "",
    fetchFacilityStatus: "",
    updateFacilityFaqsStatus: "",
    updateFacilityImagesStatus: "",
    deleteFacilityImagesStatus: "",
    updateFacilityAboutMeStatus: "",
    facilityInfo: {},
  });

  const { getStatebyId } = useCommon();

  const handleState = (obj) => {
    setGeneralProfileState((prevState) => ({
      ...prevState,
      ...obj,
    }));
  };

  const fetchFacilityInfo = async () => {
    handleState({ fetchFacilityStatus: LOADING });
    try {
      const response = await getFacilityInfo();
      const roles = response.data.data?.roles || [];

      localStorage.setItem("userRoles", JSON.stringify(roles));

      handleState({
        facilityInfo: response.data.data || {},
        fetchFacilityStatus: SUCCESS,
      });
    } catch (error) {
      handleState({ fetchFacilityStatus: ERROR });
      Promise.reject(error);
    }
  };

  const editFacilityBasicInfo = async (data) => {
    handleState({ updateFacilityBasicInfoStatus: LOADING });
    try {
      await updateFacilityBasicInfo(data);
      handleState({
        updateFacilityBasicInfoStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      handleState({ updateFacilityBasicInfoStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editFacilityContact = async (data) => {
    handleState({ updateFacilityContactStatus: LOADING });
    try {
      if (!(data?.lat && data?.lng)) {
        const state = await getStatebyId(data?.state);
        const updatedAddress = await geocodeAddress(
          `${data?.address1}, ${data?.city}, ${state?.name}, ${data?.zip_code}`
        );
        data = { ...data, lat: updatedAddress?.lat, lng: updatedAddress?.lng };
      }
      await updateFacilityContact(data);
      handleState({
        updateFacilityContactStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      handleState({ updateFacilityContactStatus: ERROR });
      sendAlert(
        (error.response?.data?.errors?.email || [])[0] ||
          error.response?.data?.message,
        "error"
      );
      Promise.reject(error);
    }
  };

  const updateTPSessionFormat = async (data) => {
    handleState({ updateBasicInfoStatus: LOADING });
    try {
      const result = await updateSessionFormats(data);
      handleState({
        updateBasicInfoStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      handleState({ updateBasicInfoStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editFacilityServices = async (data) => {
    handleState({ updateFacilityServicesStatus: LOADING });
    try {
      await updateFacilityServices(data);
      handleState({
        updateFacilityServicesStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
      fetchFacilityInfo();
    } catch (error) {
      handleState({ updateFacilityServicesStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editFacilityPhoto = async (data) => {
    handleState({ updatePhotoStatus: LOADING });
    try {
      const response = await updateFacilityPhoto(data);
      handleState({
        updatePhotoStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
      return response;
    } catch (error) {
      handleState({ updatePhotoStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const removeFacilityProfilePhoto = async () => {
    handleState({ updatePhotoStatus: LOADING });
    try {
      const response = await removeFacilityPhoto();
      handleState({
        updatePhotoStatus: SUCCESS,
      });
      sendAlert("Removed successfully", "success");
      return response;
    } catch (error) {
      handleState({ updatePhotoStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editFacilityFaqs = async (data) => {
    handleState({ updateFacilityFaqsStatus: LOADING });
    try {
      const response = await updateFacilityFaqs(data);
      if (response.data?.type === "warning") {
        sendAlert(
          response.data?.data?.message,
          "warning",
          true,
          null,
          WarningIcon
        );
        handleState({ updateFacilityFaqsStatus: ERROR });
        return;
      }
      handleState({
        updateFacilityFaqsStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      handleState({ updateFacilityFaqsStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const saveFacilityImages = async (data) => {
    handleState({ updateFacilityImagesStatus: LOADING });
    try {
      await uploadFacilityImages(data);
      handleState({
        updateFacilityImagesStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      handleState({ updateFacilityImagesStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const deleteFacilityImage = async (data) => {
    handleState({ deleteFacilityImagesStatus: LOADING });
    try {
      await removeFacilityImage(data);
      handleState({
        deleteFacilityImagesStatus: SUCCESS,
      });
      sendAlert("Deleted successfully", "success");
    } catch (error) {
      handleState({ deleteFacilityImagesStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editFacilityAboutMe = async (data) => {
    handleState({ updateFacilityAboutMeStatus: LOADING });
    try {
      const response = await updateAboutMe(data);
      if (response.data?.type === "warning") {
        sendAlert(
          response.data?.data?.message,
          "warning",
          true,
          null,
          WarningIcon
        );
        handleState({ updateFacilityAboutMeStatus: ERROR });
        return;
      }
      handleState({
        updateFacilityAboutMeStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      handleState({ updateFacilityAboutMeStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const updateGeneralProfileState = (field, value) => {
    handleState({ [field]: value });
  };

  const state = {
    ...generalProfileState,
    editFacilityBasicInfo,
    editFacilityContact,
    editFacilityServices,
    editFacilityPhoto,
    fetchFacilityInfo,
    editFacilityFaqs,
    saveFacilityImages,
    deleteFacilityImage,
    editFacilityAboutMe,
    removeFacilityProfilePhoto,
    updateGeneralProfileState,
    updateTPSessionFormat,
  };

  return (
    <GeneralProfileContext.Provider value={state}>
      {children}
    </GeneralProfileContext.Provider>
  );
};

export const useGeneralProfile = () => useContext(GeneralProfileContext);
